import React, { useRef, useEffect, useCallback } from 'react';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

interface VoiceRecorderProps {
  onSend: (blob: Blob, mimeType: string, durationSecs: number) => void;
  onCancel: () => void;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ onSend, onCancel }) => {
  const {
    isRecording,
    duration,
    audioBlob,
    analyserNode,
    startRecording,
    stopRecording,
    cancelRecording,
    error,
    mimeType,
  } = useVoiceRecorder();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const sentRef = useRef(false);

  // Auto-start recording on mount
  useEffect(() => {
    startRecording();
    return () => cancelRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show error toast and close
  useEffect(() => {
    if (error) {
      toast.error(error);
      onCancel();
    }
  }, [error, onCancel]);

  // Real-time waveform drawing
  useEffect(() => {
    if (!analyserNode || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    function draw() {
      animFrameRef.current = requestAnimationFrame(draw);
      analyserNode!.getByteTimeDomainData(dataArray);

      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;

      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx!.scale(dpr, dpr);
      }

      ctx!.clearRect(0, 0, width, height);

      const barWidth = width / bufferLength;
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0; // normalize around 1.0
        const barHeight = Math.abs(v - 1) * height;
        const y = (height - barHeight) / 2;
        ctx!.fillStyle = '#037FFF';
        ctx!.fillRect(
          i * barWidth,
          y,
          Math.max(barWidth - 1, 1),
          Math.max(barHeight, 2)
        );
      }
    }

    draw();
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [analyserNode]);

  // When audioBlob is set after stopRecording, fire onSend
  useEffect(() => {
    if (audioBlob && !sentRef.current) {
      sentRef.current = true;
      onSend(audioBlob, mimeType, Math.round(duration));
    }
  }, [audioBlob, mimeType, duration, onSend]);

  const handleCancel = useCallback(() => {
    cancelRecording();
    onCancel();
  }, [cancelRecording, onCancel]);

  const handleSend = useCallback(() => {
    stopRecording();
    // audioBlob will be set asynchronously via onstop handler,
    // the useEffect above will then call onSend
  }, [stopRecording]);

  const formatTime = (secs: number): string => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="voice-recorder">
      <button
        className="chat-btn chat-btn--icon voice-recorder__cancel-btn"
        onClick={handleCancel}
        title="Cancel"
      >
        <FontAwesomeIcon icon={faTrash} />
      </button>

      <div className="voice-recorder__visualizer">
        <span className="voice-recorder__rec-dot" />
        <canvas ref={canvasRef} className="voice-recorder__canvas" />
        <span className="voice-recorder__timer">{formatTime(duration)}</span>
      </div>

      <button
        className="chat-btn chat-btn--primary voice-recorder__send-btn"
        onClick={handleSend}
        disabled={!isRecording}
        title="Send"
      >
        <FontAwesomeIcon icon={faPaperPlane} />
      </button>
    </div>
  );
};

export default VoiceRecorder;
